import { Helmet } from 'react-helmet-async';

interface VideoObjectSchemaProps {
  /** Title of the video */
  name: string;
  /** Description of the video content */
  description: string;
  /** URL to the video file or page containing the video */
  contentUrl: string;
  /** URL to a thumbnail image */
  thumbnailUrl: string;
  /** Upload date in ISO 8601 format (YYYY-MM-DD) */
  uploadDate: string;
  /** Duration in ISO 8601 format (e.g., "PT2M30S" for 2 min 30 sec) */
  duration?: string;
  /** URL of the page where the video is embedded */
  embedUrl?: string;
  /** Whether the video is family friendly */
  isFamilyFriendly?: boolean;
  /** Transcript of the video for accessibility */
  transcript?: string;
  /** Content location if video is about a specific place */
  contentLocation?: string;
}

/**
 * VideoObject Schema Component
 * Helps search engines index video content and show video rich results
 */
const VideoObjectSchema: React.FC<VideoObjectSchemaProps> = ({
  name,
  description,
  contentUrl,
  thumbnailUrl,
  uploadDate,
  duration,
  embedUrl,
  isFamilyFriendly = true,
  transcript,
  contentLocation
}) => {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": name,
    "description": description,
    "contentUrl": contentUrl,
    "thumbnailUrl": thumbnailUrl,
    "uploadDate": uploadDate,
    "isFamilyFriendly": isFamilyFriendly,
    "publisher": {
      "@type": "Organization",
      "name": "Schoolola",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.schoolola.com.au/lovable-uploads/8577c389-5ccd-44bd-b04f-8b7121ad91d6.png"
      }
    }
  };

  if (duration) schema["duration"] = duration;
  if (embedUrl) schema["embedUrl"] = embedUrl;
  if (transcript) schema["transcript"] = transcript;
  if (contentLocation) {
    schema["contentLocation"] = {
      "@type": "Place",
      "name": contentLocation
    };
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export default VideoObjectSchema;

// Pre-configured video schemas for common videos
export const OCProgramVideoSchema: React.FC = () => (
  <VideoObjectSchema
    name="OC TMRW™ Program Overview - Schoolola Tutoring"
    description="Learn about Schoolola's proven OC TMRW™ methodology for Opportunity Class exam preparation. Our comprehensive program helps Year 4 students prepare for OC entry with expert tutors and proven strategies."
    contentUrl="https://www.schoolola.com.au/oc-program-video.mp4"
    thumbnailUrl="https://www.schoolola.com.au/lovable-uploads/nsw-hero-new-2025.jpg"
    uploadDate="2024-01-15"
    duration="PT2M"
    isFamilyFriendly={true}
  />
);

export const SelectiveProgramVideoSchema: React.FC = () => (
  <VideoObjectSchema
    name="Selective TMRW™ Program Overview - Schoolola Tutoring"
    description="Discover Schoolola's Selective TMRW™ methodology for NSW Selective School exam preparation. Our expert program helps students achieve their selective school goals with proven success."
    contentUrl="https://www.schoolola.com.au/selective-program-video.mp4"
    thumbnailUrl="https://www.schoolola.com.au/lovable-uploads/nsw-hero-new-2025.jpg"
    uploadDate="2024-01-15"
    duration="PT2M"
    isFamilyFriendly={true}
  />
);
